<template lang='pug'>
  div
    .main_body(:class='mainBodyClasses')
      app-notification
      mobile-sidebar(v-if='isMobile && renderSidebar')
      location-info-popup(v-if='!isMobileApp')

      .body-content(:class='{ "fixed": showSidebar || showMobileFilter || showImageZoom, "with-sidebar": showSidebar }')
        offline-banner(v-if='isMobile && !isMobileApp')
        app-header(v-if='!isMobileApp')
        .search-backdrop(v-if='focusSearch' @click='closeSearch')
        beauty-feed-header(v-if='shouldRenderBeautyFeedHeader()')
        AuthPopup(v-if='!isSsrSignIn()')
        Overlay(v-if='!hideOverlay')

        country-select-modal
        legal-notice(v-if='showLegalNotice && legalNotice' :legal-notice='legalNotice' @close-toast='closeLegalToast')
        main.page-container#page(:class='mainContentClasses()' @click='dyAddToBagBinding($event)' :style='`--header-height: ${headerHeight}`')
          //-
            mode='out-in' waits until it has removed the old component before showing the new one
            @before-enter is the best time to scroll without the appearance of a "jump" on the page
          transition(
            name='router-fade'
            mode='out-in'
            @before-enter='triggerScroll'
          )
            slot(name='page-content')
        .clearfix
        app-footer(v-if='renderBelowTheFold && !isMobileApp')

    .landscape-msg-overlay(v-if='isMobile')
      .change-orientation-icon
      .change-orientation-msg(v-text='$t("notifications.mobilePortrait")')
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import get from 'lodash/get'
import { getCookie } from '@/modules/services/cookies'
import { searchUtil } from '@/mixins/layoutUtil'
import { dyProductBindings } from '@/mixins/dyUtil'
import makeCanonicalUrl from '@/modules/services/canonical-url'
import serviceWorkerUtil from '@/mixins/serviceWorkerUtil'
import AppHeader from '@/components/layout/header/main.vue'
import AppNotification from '@/components/layout/notification/main.vue'
import OfflineBanner from '@/components/layout/offline-banner.vue'
import LocationInfoPopup from '@/components/layout/location-info-popup.vue'
import CountrySelectModal from '@/components/layout/header/country-select-modal.vue'
import Overlay from '../components/shared/overlay.vue'
import LegalNotice from '@/components/legal-notice/LegalNotice.vue'

const BeautyFeedHeader = () => import(
  /* webpackChunkName: 'beauty-feed-header' */ '@/components/beauty-feed/header/main.vue'
)

const MobileSidebar = () => import(
  /* webpackChunkName: 'layout-mobile-sidebar' */ '@/components/layout/sidebar/main.vue'
)

const AppFooter = () => import(
  /* webpackChunkName: 'layout-footer' */ '@/components/layout/footer/main.vue'
)

const AUTH_MODAL_PATH = '#authentication'

export default {
  components: {
    MobileSidebar,
    AppHeader,
    AppFooter,
    AppNotification,
    OfflineBanner,
    LocationInfoPopup,
    BeautyFeedHeader,
    CountrySelectModal,
    Overlay,
    LegalNotice,
    AuthPopup: () => import(/* webpackChunkName: 'layout-auth-popup' */ '@/components/account/authentication/AuthPopup.vue')
  },

  mixins: [
    searchUtil,
    serviceWorkerUtil,
    dyProductBindings
  ],

  data () {
    return {
      legalNotice: '',
      showLegalNotice: false,
      headerHeight: '130px',
      headerHeightObserver: null
    }
  },

  head: {
    title () {
      return {
        inner: this.seoMetaTitle,
        separator: this.seoOverride,
        complement: this.seoOverride
      }
    },
    meta () {
      const meta = [
        { name: 'description', content: this.seoMetaDescription, id: 'meta-description' },
        { name: 'keywords', content: this.seoMetaKeywords, id: 'meta-keywords' },
        { name: 'og:title', content: this.seoOgTitle, id: 'og-title' },
        { name: 'og:type', content: this.seoOgType, id: 'og-type' },
        { name: 'og:url', content: this.seoOgUrl, id: 'og-url' },
        { name: 'og:image', content: this.seoOgImage, id: 'og-image' },
        { name: 'prerender-status-code', content: this.prerenderStatusCode, id: 'prerender-status-code' },
        { name: 'branch:deeplink:deeplink_path', content: this.branchMetaContent, id: 'branch-meta' }
      ]

      if (this.hasIosApp) {
        meta.push({ name: 'al:ios:url', content: this.seoDeeplinkUrl, id: 'al-ios-url' })
      }

      if (this.hasAndroidApp) {
        meta.push({ name: 'al:android:url', content: this.seoDeeplinkUrl, id: 'al-android-url' })
      }

      return meta
    },
    link () {
      return [
        { rel: 'canonical', href: `${makeCanonicalUrl(this.seoOgUrl, this.$route)}`, id: 'meta-canonical' }
      ]
    }
  },

  computed: {
    ...mapState({
      currentUser: state => state.user?.currentUser,
      seoDeeplinkUrl: state => state.layout.seoDeeplinkUrl,
      seoMetaTitle: state => state.layout.seoMetaTitle,
      seoMetaDescription: state => state.layout.seoMetaDescription,
      seoMetaKeywords: state => state.layout.seoMetaKeywords,
      seoOgTitle: state => state.layout.seoOgTitle,
      seoOgType: state => state.layout.seoOgType,
      seoOgUrl: state => state.layout.seoOgUrl,
      seoOgImage: state => state.layout.seoOgImage,
      seoUpdate: state => state.layout.seoUpdate,
      showSidebar: state => state.layout.showSidebar,
      showMobileFilter: state => state.layout.showMobileFilter,
      showImageZoom: state => state.layout.showImageZoom,
      isOffline: state => state.layout.isOffline,
      renderSidebar: state => state.layout.renderSidebar,
      renderBelowTheFold: state => state.layout.renderBelowTheFold,
      focusSearch: state => state.search?.focusSearch,
      routerNavigation: state => state.layout.routerNavigation,
      visitorIsBot: state => state.layout.visitorIsBot,
      prerenderStatusCode: state => state.layout.prerenderStatusCode,
      hasIosApp: state => state.layout.hasIosApp,
      hasAndroidApp: state => state.layout.hasAndroidApp,
      branchMetaContent: state => state.layout.branchMetaContent
    }),
    ...mapGetters(['isMobileApp']),
    ...mapGetters([
      'currentGlobalConfig'
    ]),
    isMobile: () => vueSupport.isMobile,
    seoOverride () {
      // Don't add separator and complement for home page
      return this.$route.name === 'home-index' ? ' ' : null
    },
    mainBodyClasses () {
      return {
        'with-sidebar': this.showSidebar,
        'with-offline-banner': this.isOffline,
        'no-top-padding': this.isMobileApp
      }
    },
    hideOverlay () {
      return this.$route.hash === AUTH_MODAL_PATH
    }
  },

  watch: {
    seoUpdate () {
      if (this.seoUpdate) {
        this.$emit('updateHead')
        this.$store.commit('setSeoUpdate', false)
      }
    },

    visitorIsBot () {
      // display supplemental content if a page is crawled by Googlebot
      if (this.visitorIsBot) {
        this.showLazyComponents()
      }
    }
  },

  async created () {
    window.addEventListener('scroll', this.showLazyComponents, { once: true })

    window.addEventListener('online', this.onOnline)
    window.addEventListener('offline', this.onOffline)

    this.$store.commit('platform', vueSupport.platform)
    this.$store.dispatch('fetchCategories')

    this.fetchUser()
    this.fetchLayoutConfig()

    await this.$store.dispatch('initializeGlobalConfig')
    this.isLegalNoticeVisible()
    this.legalNoticeMessage()
  },

  mounted () {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    const fullLoadDuration = 20000 // On average, 95% of users fully load the page within 20 seconds. Source: New Relic Browser (https://rpm.newrelic.com/accounts/57886/browser/289855)
    setTimeout(() => {
      this.showLazyComponents()
    }, fullLoadDuration)

    if (!navigator.onLine) {
      this.onOffline()
    }

    this.$router.afterEach((to, from) => {
      this.$nextTick(() => {
        if (from.name && !this.routerNavigation) {
          this.$store.commit('setRouterNavigation', true)
        }
      })
    })

    this.$nextTick(() => {
      this.setHeaderObserver()
    })
  },

  destroyed () {
    window.removeEventListener('online', this.onOnline)
    window.removeEventListener('offline', this.onOffline)
    this.headerHeightObserver.disconnect()
  },

  methods: {
    showLazyComponents () {
      this.$store.commit('setRenderBelowTheFold', true)
      this.$store.commit('setRenderAuthentication', true)
    },

    triggerScroll () {
      this.$root.$emit('triggerScroll')
    },

    onOffline () {
      this.$store.commit('setIsOffline', true)
    },

    onOnline () {
      this.$store.commit('setIsOffline', false)
    },

    async fetchLayoutConfig () {
      try {
        this.$store.dispatch('fetchLayoutConfig')
      } catch (error) {}
    },

    async fetchUser () {
      // persisting user information is served on rails
      const user = vueSupport.currentUser.data

      this.$store.commit('setCurrentUser', user)

      if (user) {
        const { default: GTM } = await import('@/modules/services/gtm')
        GTM.updateUser(user)
      }
    },

    isSsrSignIn () {
      return this.$route.path === '/sign_in'
    },

    isBeautyFeed () {
      return this.$route.path.includes('/beautyfeed')
    },

    isBeautyFeedArticle () {
      return this.$route.name === 'beautyfeed-show'
    },

    isOlapicInstagramGallery () {
      return this.$route.name === 'olapic-instagram-gallery'
    },

    shouldRenderBeautyFeedHeader () {
      if (this.isMobile) {
        return this.isBeautyFeed() && !this.isBeautyFeedArticle()
      } else {
        return this.isBeautyFeed()
      }
    },

    shouldIncludeContainerClass () {
      return !(this.isBeautyFeed() || this.isOlapicInstagramGallery())
    },

    shouldExcludeTopPadding () {
      const isBeautyFeedPage = (this.isBeautyFeed() && !(this.isMobile && this.isBeautyFeedArticle()))
      return isBeautyFeedPage || this.isOlapicInstagramGallery() || this.isMobileApp
    },

    mainContentClasses () {
      return {
        'container': this.shouldIncludeContainerClass(),
        'with-sign-in-banner': !this.currentUser,
        'no-top-padding': this.shouldExcludeTopPadding()
      }
    },

    openAuth () {
      this.$store.commit('setShowAuthPopup', true)
      this.$store.commit('setRenderAuthentication', true)

      this.$router.replace({ query: this.$router.currentRoute.query, hash: AUTH_MODAL_PATH })
    },

    openAuthModal () {
      if ($(AUTH_MODAL_PATH)) {
        $(AUTH_MODAL_PATH).modal('show')
        this.openAuth()
      }
    },

    isLegalNoticeVisible () {
      const cookie = getCookie('legal_notice')
      if (!cookie) {
        this.showLegalNotice = true
      }
    },

    legalNoticeMessage () {
      const language = I18n.language.split('_')[1]
      const message = get(this.currentGlobalConfig, `legalNotice.${language}`)
      if (message) {
        this.legalNotice = message
      }
    },

    closeLegalToast () {
      this.showLegalNotice = false
    },

    setHeaderHeight () {
      this.headerHeight = `${document.getElementById('header').offsetHeight}px`
    },

    setHeaderObserver () {
      const targetNode = document.getElementById('header')
      const config = { attributes: true, childList: true, subtree: true }
      const callback = (mutationList, observer) => {
        mutationList.forEach(() => this.setHeaderHeight())
      }
      this.headerHeightObserver = new MutationObserver(callback)
      if (targetNode) {
        this.headerHeightObserver.observe(targetNode, config)
      }
    }
  }
}
</script>

<style lang='scss'>
  .disable-scroll {
    overflow: hidden;
  }

  // Lazy loading
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  img[lazy='loaded'] {
    animation-duration: 0.3s;
    animation-fill-mode: both;
    animation-name: fadeIn;
  }
</style>

<style lang='scss' scoped>
  .router-fade-enter-active,
  .router-fade-leave-active {
    transition: opacity 0.4s ease-in;
  }

  .router-fade-enter,
  .router-fade-leave-to {
    opacity: 0;
  }

  .search-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    z-index: $zindex-sticky;  // hides the rest of the mobile site while keeping the header visible

    @include media-breakpoint-up(sm) {
      top: 35px;
      background-color: rgba(255, 255, 255, 0.5);
      z-index: $zindex-modal-backdrop;
    }
  }

  .main_body {
    min-width: 1125px;
    overflow: hidden;
    position: relative;

    @include media-breakpoint-down(lg) {
      min-width: inherit;
    }

    @include media-breakpoint-down(sm) {
      @include ease-in-out-quad-transition;
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      min-height: 100%;
      min-width: inherit;

      &.with-offline-banner {
        padding-top: $offline-banner-height;

        #header {
          top: $offline-banner-height;
        }
      }

      &.with-sidebar {
        overflow-x: hidden;
      }

      &.no-top-padding {
        padding-top: 0;
      }

      #header {
        @include ease-in-out-quad-transition;
      }
    }
  }

  .body-content {
    position: relative;
    width: 100%;
    background-color: #fff;
    left: 0;
    transition: left 0.35s ease;

    &.with-sidebar {
      left: $sidebar-offset-left;
    }

    &.fixed {
      position: absolute;
      top: 0;
      bottom: 0;
      overflow: hidden;
    }
  }

  .page-container {
    padding-top: var(--header-height);
    min-height: 100vh;

    &.no-top-padding {
      padding-top: 0;
    }

    @include media-breakpoint-up(md) {
      padding-top: 10px;
    }

    // Fix grid for IE
    > div {
      @include media-breakpoint-up(md) {
        max-width: 690px;
        margin-left: auto;
        margin-right: auto;
      }

      @include media-breakpoint-up(lg) {
        max-width: 930px;
      }

      @include media-breakpoint-up(xl) {
        max-width: 1125px;
      }
    }

    /deep/ .mobile-filter-sort {
      top: var(--header-height);
    }
  }

  .landscape-msg-overlay {
    display: none;

    @include media-breakpoint-up(sm) {
      display: block;
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: rgba(255, 255, 255, 0.95);
      z-index: $zindex-popover;

      .change-orientation-icon,
      .change-orientation-msg {
        width: 360px;
        height: 128px;
        margin: 20px auto;
        color: red;
        font-size: 24px;
        text-align: center;
      }

      .change-orientation-icon {
        width: 128px;
        margin-top: 40px;
        background-image: url('../assets/images/rotate.png');
      }
    }
  }
</style>
